import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import * as Enums from "@/utils/enums";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import { useDebouncedState } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import featureService from "@/services/feature/feature-service";
import constants from "@/utils/constants";

const JobMaterialsSelectTable: FC<
    {
        filteredJobInventory
        itemId
        onSelectedItemsChanged: (allSelectedItems: any[]) => void
        blockFullyInvoicedSelection?: boolean
        module: number
        resetSelections?: number
    }> = (props: any) => {

        const { data: hasStockControl } = useQuery(['hasStockControl'], () => featureService.getFeature(constants.features.STOCK_CONTROL));

        const [currentData, setCurrentData] = useState(props.filteredJobInventory)
        const [currentSections, setCurrentSections] = useState<any[]>([])

        // Use resetSelections prop as a key for the SectionTable component
        // When resetSelections changes, the SectionTable will be re-mounted and its internal state will be reset

        /** force send data when leaving the page without saving*/
        const handleRouteChange = useCallback(() => {
            if (changed.current) {
                console.log('leaving page before saving, posting data changes')
            }
        }, [currentData, currentSections])
        const router = useRouter()
        useEffect(() => {
            router.events.on('routeChangeStart', handleRouteChange)
            // If the component is unmounted, unsubscribe
            // from the event with the `off` method:
            return () => {
                router.events.off('routeChangeStart', handleRouteChange)
            }
        }, [router, handleRouteChange])

        /** debounce all data updates and only trigger onUpdate callback when data was changed internally */
        // const [debounced, setDebounced] = useDebouncedState([currentData/*, currentSections*/], 1500)
        const [debounced, setDebounced] = useDebouncedState(currentData/*, currentSections*/, 1500)
        const changed = useRef(false)
        useEffect(() => {
            setDebounced(currentData/*, currentSections*/)
        }, [currentData/*, currentSections*/])
        useEffect(() => {
            if (changed.current) {
                changed.current = false
            }
        }, [debounced]);

        /** update current data when data changes externally - update only row version if data change was triggered with table data update internally*/
        const cancelNextUpdate = useRef(false)
        useEffect(() => {
            if (props.filteredJobInventory) {
                if (!cancelNextUpdate.current) {
                    setCurrentData(props.filteredJobInventory)
                    // props.filteredJobInventory.length !== currentData.length && // if length changed the data was modified form outside and history needs to be appended
                } else {
                    cancelNextUpdate.current = false
                }
            }
        }, [props.filteredJobInventory])

        const handleDataUpdate = (newData: any, /*newSections, updateHistory = true*/) => {
            setCurrentData(newData)
            changed.current = true
            cancelNextUpdate.current = true
            // updateHistory && setHistory((p) => [...p, [newData, newSections]])
        }

        const handleInputChange = (key: any, item: any, newValue: any) => {
            if (key === 'QuantityRequested') {
                handleDataUpdate(
                    currentData.map((x: any) => x.ID === item.ID ? ({ ...x, [key]: newValue }) : x)
                    /*,currentSections*/
                )
            }
        }

        const handleItemAction = (actionName: string, item: any, itemIndex: number, group: any) => {
            if (actionName === 'code') {
            } else if (actionName === 'delete') {
                handleDataUpdate(currentData.filter((x: any) => x.ID !== item.ID)/*, newSections*/)
            }
        }
        const handleSectionAction = (name: any, group: any) => {
            if (name === 'material') {
            } else if (name === 'delete') {
                handleDataUpdate(
                    currentData.filter((x: any) => x.InventorySectionID !== group.id).map((x: any, i: any) => ({ ...x, LineNumber: i + 1 })),
                )
            }
        }
        //.filter(x => props.blockFullyInvoicedSelection !== true || x.QuantityRequested > x.QuantityInvoiced)
        return <>
            <SectionTable
                key={`job-materials-select-table-${props.resetSelections}`}
                data={currentData}
                itemsSelected={props.onSelectedItemsChanged}
                itemsSelectedFilter={props.blockFullyInvoicedSelection ? (item: any) => {
                    if (props.module !== Enums.Module.Invoice && item?.QuantityRequested === undefined || item?.QuantityInvoiced === undefined || item?.QuantityInvoicedDraft === undefined) return true;
                    return !hasStockControl || item.QuantityRequested > (item.QuantityInvoiced + item.QuantityInvoicedDraft);
                } : undefined}
                lineClamp={3}
                mapping={[
                    {
                        key: 'InventoryCode',
                        label: 'Code',
                        valueFunction: (item: any) => item.ProductID ? item.ProductNumber : item.InventoryCode,
                        linkAction: 'code'
                    },
                    {
                        key: 'Description',
                        label: 'Description',
                        valueFunction: (item: any) => <div style={{maxWidth: "200px"}} title={item.Description || item.InventoryDescription}>{item.Description || item.InventoryDescription}</div>,
                        maxColumnWidth: 50
                    },
                    {
                        key: 'DynamicType',
                        label: 'Type',
                        type: 'stockItemType',
                        valueFunction: (item: any) => item.ProductID ? 'Asset' : Enums.getEnumStringValue(Enums.StockItemType, item.Inventory?.StockItemType)
                    },
                    {
                        key: 'QuantityRequested',
                        label: 'Qty',
                        min: 0,
                        alignRight: true,
                    },
                    ...(hasStockControl && props.module === Enums.Module.Invoice && [
                        {
                            key: 'QuantityInvoiced',
                            label: 'Invoiced',
                            min: 0,
                            valueFunction: (item: any) => item.QuantityInvoiced + item.QuantityInvoicedDraft,
                            alignRight: true,
                        },
                        {
                            key: 'Quantity',
                            label: 'Add to Invoice',
                            min: 0,
                            valueFunction: (item: any) => {
                                let diff = item.QuantityRequested - item.QuantityInvoiced - item.QuantityInvoicedDraft;
                                if (diff < 0) diff = 0;
                                return <div style={{ fontWeight: "bold", color: (diff > 0 ? "green" : "orange") }}>{diff}</div>
                            },
                            alignRight: true,
                        }
                    ] as any || []),
                    ...(hasStockControl && props.module === Enums.Module.Quote && [
                        {
                            key: 'QuantityQuoted',
                            label: 'Quoted',
                            min: 0,
                            valueFunction: (item: any) => item.QuantityQuoted,
                            alignRight: true,
                        }
                    ] as any || []),
                    ...(hasStockControl && props.module === Enums.Module.PurchaseOrder && [
                        {
                            key: 'QuantityOnPurchaseOrder',
                            label: 'Purchased',
                            min: 0,
                            valueFunction: (item: any) => item.QuantityOnPurchaseOrder,
                            alignRight: true,
                        }
                    ] as any || [])

                ]}
                controls={[]}
                sectionControls={[]}
                onSectionItem={handleSectionAction}
                onDataUpdate={handleDataUpdate}
                onAction={handleItemAction}
                canEdit={false}
                stylingProps={{
                    compact: true,
                    rows: false,
                    darkerText: true
                }}
                height={'100%'}
                sectionTitleKey={'InventorySectionName'}
                sectionIdKey={'InventorySectionID'}
                itemId={props.itemId}
                module={Enums.Module.JobCard}
            />

        </>
    }
export default JobMaterialsSelectTable
